"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"
import { Icon } from "~/react/ui/icon"
import { Button } from "./button"
import { Slot } from "@radix-ui/react-slot"

const Sheet = SheetPrimitive.Root
const SheetTrigger = SheetPrimitive.Trigger
const SheetClose = SheetPrimitive.Close
const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn("sidepanel__overlay is-visible", className)}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva("sidepanel position-fixed", {
  variants: {
    size: {
      md: "sidepanel--md",
      lg: "sidepanel--lg",
    },
    side: {
      top: "top-0",
      bottom: "bottom-0",
      left: "top-0 start-0",
      right: "top-0 end-0",
    },
  },
  defaultVariants: {
    side: "right",
  },
})

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps & { overlay?: boolean }
>(
  (
    { side = "right", size, className, overlay = true, children, ...props },
    ref,
  ) => {
    // When overlay is false, we want an "interactive sidepanel" where users
    // can interact with the page content without the sidepanel closing.
    // We prevent the default behavior of closing on outside interaction.
    const handleInteractOutside = overlay
      ? props.onInteractOutside
      : (e: Event) => {
          e.preventDefault()
          props.onInteractOutside?.(e as CustomEvent)
        }

    return (
      <SheetPortal>
        {overlay && <SheetOverlay />}
        <SheetPrimitive.Content
          ref={ref}
          className={cn(sheetVariants({ side, size }), className)}
          data-side={side}
          aria-describedby={undefined}
          {...props}
          onInteractOutside={handleInteractOutside}
        >
          {children}
          <SheetPrimitive.Close className="sidepanel__close" asChild>
            <Button variant="ghost" iconOnly size="sm">
              <Icon icon="times" size="sm" />
              <span className="sr-only">Close</span>
            </Button>
          </SheetPrimitive.Close>
        </SheetPrimitive.Content>
      </SheetPortal>
    )
  },
)
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("sidepanel__header", className)} {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetHeading = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("sidepanel__heading", className)} {...props} />
)
SheetHeading.displayName = "SheetHeading"

const footerVariants = cva("sidepanel__footer", {
  variants: {
    sticky: {
      true: "sidepanel__footer--sticky",
      false: "",
    },
  },
  defaultVariants: {
    sticky: false,
  },
})
const SheetFooter = ({
  className,
  sticky = false,
  ...props
}: {
  className?: string
  sticky?: boolean
  children: React.ReactNode
}) => (
  <footer className={cn(footerVariants({ sticky }), className)} {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("sidepanel__title", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("sidepanel__description", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

const SheetBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("sidepanel__body", className)} {...props} />
))
SheetBody.displayName = "SheetBody"

const SheetAlert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <Slot ref={ref} className={cn("sidepanel__alert", className)} {...props} />
))
SheetAlert.displayName = "SheetAlert"

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetHeading,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetAlert,
  SheetBody,
}
